import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Floating-label input — label floats up when focused OR when the input has a value.
 * All colours come from CSS design tokens — no dark: classes.
 */
export default function Input({
    label,
    icon: Icon,
    error,
    type = 'text',
    register,      // react-hook-form register return value
    rightAction,
    index = 0,
    value: externalValue,  // optionally controlled from outside
    ...rest
}) {
    const [focused, setFocused] = useState(false);
    const [internalVal, setInternalVal] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // A label "floats" when focused OR when there is any typed value
    const hasValue = focused || internalVal.length > 0 || (externalValue && String(externalValue).length > 0);

    const isPassword = type === 'password';
    const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : type;

    // Border colour driven by state
    const borderClass = error
        ? 'border-error'
        : focused
            ? 'border-border-focus'
            : 'border-border';

    const iconColor = error
        ? 'text-error'
        : focused
            ? 'text-brand'
            : 'text-muted';

    // Intercept onChange to track the internal value for the floating label
    function handleChange(e) {
        setInternalVal(e.target.value);
        rest.onChange?.(e);
    }

    // If register is provided, merge its onChange
    const registerProps = register
        ? {
            ...register,
            onChange: (e) => {
                setInternalVal(e.target.value);
                register.onChange?.(e);
            },
        }
        : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.07 }}
            className="relative w-full"
        >
            <div className={`relative flex items-center rounded-xl border-2 bg-surface transition-all duration-200 ${borderClass}`}>

                {/* Floating label */}
                <span
                    className={`
            absolute pointer-events-none select-none font-medium
            transition-all duration-200 origin-left
            ${hasValue
                            ? 'top-0 left-10 -translate-y-[50%] text-[11px] px-1.5 py-0 bg-overlay text-brand scale-90 rounded'
                            : 'top-1/2 left-10 -translate-y-1/2 text-sm text-muted'
                        }
          `}
                >
                    {label}
                </span>

                {/* Left icon */}
                <span className="pl-4 flex-shrink-0 flex items-center">
                    <Icon className={`w-4 h-4 transition-colors duration-200 ${iconColor}`} />
                </span>

                {/* Input field */}
                <input
                    {...(registerProps || {})}
                    {...(!register ? rest : {})}
                    type={resolvedType}
                    value={externalValue !== undefined ? externalValue : undefined}
                    onChange={register ? registerProps.onChange : handleChange}
                    onFocus={() => setFocused(true)}
                    onBlur={(e) => {
                        setFocused(false);
                        setInternalVal(e.target.value);  // sync on blur
                        register?.onBlur?.(e);
                    }}
                    className="w-full px-3 py-3.5 bg-transparent text-sm text-primary focus:outline-none placeholder-transparent"
                    placeholder={label}
                    id={register?.name || rest.id}
                    name={register?.name || rest.name}
                    ref={register?.ref || rest.ref}
                />

                {/* Right: password toggle or custom action */}
                {isPassword ? (
                    <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword((v) => !v)}
                        className="pr-4 text-muted hover:text-brand transition-colors flex-shrink-0"
                    >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                ) : rightAction ? (
                    <span className="pr-3 flex-shrink-0">{rightAction}</span>
                ) : null}
            </div>

            {/* Error message */}
            <AnimatePresence>
                {error && (
                    <motion.p
                        key="error"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.2 }}
                        className="mt-1 ml-1 text-xs text-error font-medium"
                    >
                        {error}
                    </motion.p>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
